import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import {
  getFinanceCashOverview,
  getFinanceCashFlow,
  getRondoPayoffsVyuctovani,
  type FinanceCashCountry,
  type FinanceCashRiskBucket,
  type CashFlowResult,
  type CashFlowGranularity,
  type CashFlowCountry,
  type RondoPayoffVyuctovaniRow,
} from '../api/rondo';
import { useFeather } from '../shell/useFeather';
import { ServiceBar } from '../shell/ServiceBar';
import { useTabParam } from '../shell/useTabParam';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { useRondoPageData } from './useRondoPageData';
import './finance-uzivatele.css';

type SubTab = 'prehled' | 'pohyby' | 'vyuctovani';
const FIN_UZIV_TABS: readonly SubTab[] = ['prehled', 'pohyby', 'vyuctovani'];

const FLAGS: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const NAMES: Record<string, string> = { CZ: 'Česko', SK: 'Slovensko', RO: 'Rumunsko' };

const fmtN = (n: number) => n.toLocaleString('cs-CZ');
const fmtM = (n: number, currency: string) => `${Math.round(n).toLocaleString('cs-CZ')} ${currency}`;
const EMPTY_COUNTRIES: FinanceCashCountry[] = [];

export function FinanceUzivatelePage() {
  const [tab, setTab] = useTabParam<SubTab>('tab', FIN_UZIV_TABS, 'prehled');
  const { data, error, loading } = useRondoPageData<FinanceCashCountry[]>('finance-users-overview', getFinanceCashOverview);
  const countries = data ?? EMPTY_COUNTRIES;

  useFeather([countries, tab]);

  return (
    <section className="rondo-finance-users rondo-arc-finance-users p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">FINANCE</div><h1>Uživatelé</h1><p>Zůstatky, peněžní pohyby a vyúčtování uživatelů</p></div></header>
      <ArcTabs label="Finance uživatelů" items={[{ value: 'prehled', label: 'Přehled' }, { value: 'pohyby', label: 'Pohyby' }, { value: 'vyuctovani', label: 'Vyúčtování' }]} value={tab} onChange={setTab} className="rondo-finance-arc-tabs" />

      {tab === 'vyuctovani' ? (
        <VyuctovaniTab />
      ) : tab === 'pohyby' ? (
        <PohybyTab />
      ) : loading ? (
        <PageState title="Načítám finance uživatelů" loading />
      ) : error ? (
        <PageState title="Finance uživatelů se nepodařilo načíst" message={error instanceof ApiError ? error.message : 'Načtení selhalo'} />
      ) : (
        <div className="rondo-arc-finance-market-grid">
          {countries.map((c) => (
            <CountryCard key={c.country} c={c} />
          ))}
        </div>
      )}
    </section>
  );
}
const FX: Record<string, number> = { EUR: 25, RON: 5, CZK: 1 };
const COUNTRY_OPTS: { value: CashFlowCountry; label: string }[] = [
  { value: 'ALL', label: 'Všechny' },
  { value: 'CZ', label: 'CZ' },
  { value: 'SK', label: 'SK' },
  { value: 'RO', label: 'RO' },
];

function fmtPeriodLabel(iso: string, g: CashFlowGranularity): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return g === 'month' ? `${m}/${y}` : `${Number(d)}.${Number(m)}.${y}`;
}

function fmtRate(numer: number, denom: number): ReactNode {
  if (!denom) return <span className="text-muted">-</span>;
  const r = numer / denom;
  const cls = r > 1.05 ? 'text-success' : r < 0.95 ? 'text-danger' : 'text-muted';
  return <span className={cls}>{r.toFixed(2)}×</span>;
}

function PohybyTab() {
  const [country, setCountry] = useState<CashFlowCountry>('ALL');
  const [granularity, setGranularity] = useState<CashFlowGranularity>('day');
  const [showCzk, setShowCzk] = useState(false);
  const [data, setData] = useState<CashFlowResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFinanceCashFlow({ granularity, country })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [granularity, country]);

  useFeather([data]);

  const nativeCur = country === 'ALL' ? 'CZK' : country === 'CZ' ? 'CZK' : country === 'SK' ? 'EUR' : 'RON';
  const displayCur = showCzk ? 'CZK' : nativeCur;
  const conv = (v: number) => (showCzk && country !== 'ALL' && nativeCur !== 'CZK' ? Math.round(v * (FX[nativeCur] || 1)) : v);
  const fmtM = (v: number) => `${Math.round(conv(v)).toLocaleString('cs-CZ')} ${displayCur}`;

  const periods = data?.periods ?? [];
  const periodRows = periods.map((period) => ({ ...period }));
  type PeriodRow = (typeof periodRows)[number];
  const periodColumns: DataColumn<PeriodRow>[] = [
    { key: 'period', label: 'Období', width: 130, render: (_, row) => fmtPeriodLabel(row.period, granularity) },
    { key: 'totalBalance', label: 'Zůstatky', numeric: true, width: 145, render: (_, row) => fmtM(row.totalBalance) },
    { key: 'outCount', label: '90 out #', numeric: true, width: 105, render: (_, row) => fmtN(row.outCount) },
    { key: 'outSum', label: '90 out $', numeric: true, width: 145, render: (_, row) => fmtM(row.outSum) },
    { key: 'inCount', label: '90 in #', numeric: true, width: 105, render: (_, row) => fmtN(row.inCount) },
    { key: 'inSum', label: '90 in $', numeric: true, width: 145, render: (_, row) => fmtM(row.inSum) },
    { key: 'rateCount', label: 'Rate #', numeric: true, width: 95, render: (_, row) => fmtRate(row.outCount, row.inCount), sortable: false },
    { key: 'rateSum', label: 'Rate $', numeric: true, width: 95, render: (_, row) => fmtRate(row.outSum, row.inSum), sortable: false },
  ];

  function reset() {
    setCountry('ALL');
    setGranularity('day');
    setShowCzk(false);
  }

  return (
    <>
      <ServiceBar variant="seg" selects={[
        { label: 'Země', value: country, onChange: (value) => setCountry(value as CashFlowCountry), options: COUNTRY_OPTS },
        { label: 'Granularita', value: granularity, onChange: (value) => setGranularity(value as CashFlowGranularity), options: [{ value: 'day', label: 'Den' }, { value: 'month', label: 'Měsíc' }] },
      ]} extra={<label className="finance-switch"><input type="checkbox" checked={showCzk} onChange={(event) => setShowCzk(event.target.checked)} disabled={country === 'ALL'} /><i /><span>Zobrazit v CZK</span></label>} onReset={reset} right={
            <div className="finance-filter-meta">
              {loading ? 'Načítám…' : `${periods.length} období · ${granularity === 'day' ? 'den' : 'měsíc'} · ${country} · měna `}
              {!loading && <strong>{displayCur}</strong>}
              {!loading && showCzk && country !== 'ALL' && ` (kurz EUR=${FX.EUR}, RON=${FX.RON})`}
            </div>
      } />

      <div className="rondo-arc-finance-table">
        {error && <p role="alert" className="rondo-arc-finance-error">{error}</p>}
        {loading ? <PageState title="Načítám peněžní pohyby" loading /> : <SortableDataTable rows={periodRows} columns={periodColumns} rowKey="period" caption="Peněžní pohyby uživatelů" emptyMessage="Žádná data." />}
      </div>
    </>
  );
}
function CountryCard({ c }: { c: FinanceCashCountry }) {
  const pctAbove = c.withBalance > 0 ? Math.round((c.aboveCount / c.withBalance) * 100) : 0;
  return (
    <Card title={NAMES[c.country] ?? c.country} description={`${FLAGS[c.country] ?? ''} ${c.country} · se zůstatkem ${fmtN(c.withBalance)} z ${fmtN(c.totalActive)}`} className="rondo-arc-finance-market-card">
      <div className="rondo-arc-finance-balance-grid">
        <div><small>Pod limitem {fmtM(c.threshold, c.currency)}</small><strong>{fmtN(c.belowCount)}</strong><span>{fmtM(c.belowSum, c.currency)}</span></div>
        <div><small>Může vybrat · {pctAbove} %</small><strong>{fmtN(c.aboveCount)}</strong><span>{fmtM(c.aboveSum, c.currency)}</span></div>
      </div>
      <div className="rondo-arc-finance-risk-list"><h4>Stáří zůstatků</h4>
        <RiskRow label="0–30 dní · aktivní" item={c.risk.d0_30} currency={c.currency} />
        <RiskRow label="31–60 dní · nečinný" item={c.risk.d31_60} currency={c.currency} />
        <RiskRow label="61–90 dní · před cleanupem" item={c.risk.d61_90} currency={c.currency} />
        {c.risk.d90plus.count > 0 && <RiskRow label="90+ dní · zisk Ronda" item={c.risk.d90plus} currency={c.currency} />}
      </div>
    </Card>
  );
}

function RiskRow({ label, item, currency }: {
  label: string; item: FinanceCashRiskBucket; currency: string;
}): ReactNode {
  return (
    <div className="rondo-arc-finance-risk-row">
      <span>{label}</span><div><strong>{fmtN(item.count)}</strong><small>{fmtM(item.sum, currency)}</small></div>
    </div>
  );
}

type VyuctGranularity = 'day' | 'month';
const VYUCT_CURRENCY_OPTS = ['ALL', 'CZK', 'EUR'] as const;
type VyuctCurrency = typeof VYUCT_CURRENCY_OPTS[number];

function VyuctovaniTab() {
  const [granularity, setGranularity] = useState<VyuctGranularity>('month');
  const [currency, setCurrency] = useState<VyuctCurrency>('ALL');
  const [showCzk, setShowCzk] = useState(false);
  const [rows, setRows] = useState<RondoPayoffVyuctovaniRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRondoPayoffsVyuctovani({ granularity })
      .then((data) => !cancelled && setRows(data))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [granularity]);

  useFeather([rows, currency, showCzk]);

  const filtered = currency === 'ALL' ? rows : rows.filter((r) => r.currency === currency);
  const view = showCzk
    ? Object.values(filtered.reduce<Record<string, RondoPayoffVyuctovaniRow>>((acc, r) => {
        const rate = FX[r.currency] ?? 1;
        const key = r.period;
        if (!acc[key]) acc[key] = { period: r.period, currency: 'CZK', count: 0, volume: 0 };
        acc[key].count += r.count;
        acc[key].volume += r.volume * rate;
        return acc;
      }, {})).sort((a, b) => (a.period < b.period ? 1 : -1))
    : filtered;

  const totCount = view.reduce((s, r) => s + r.count, 0);
  const totVolByCur = view.reduce<Record<string, number>>((acc, r) => { acc[r.currency] = (acc[r.currency] || 0) + r.volume; return acc; }, {});
  const reset = () => { setCurrency('ALL'); setGranularity('month'); setShowCzk(false); };
  const tableRows = view.map((row, index) => ({ ...row, key: `${row.period}-${row.currency}-${index}` }));
  type SettlementRow = (typeof tableRows)[number];
  const columns: DataColumn<SettlementRow>[] = [
    { key: 'period', label: 'Období', width: 170 },
    { key: 'currency', label: 'Měna', width: 100 },
    { key: 'count', label: 'Počet', numeric: true, width: 150, render: (_, row) => fmtN(row.count) },
    { key: 'volume', label: 'Objem', numeric: true, width: 190, render: (_, row) => fmtM(row.volume, row.currency) },
  ];

  return (
    <>
      {!loading && <div className="rondo-arc-finance-settlement-summary"><MetricCard label="Doručené platby" value={totCount} context="Ve zvoleném období" /><Card title="Celkový objem" description="Podle měny">{Object.entries(totVolByCur).length ? Object.entries(totVolByCur).map(([cur, volume]) => <div key={cur}><span>{cur}</span><strong>{fmtM(volume, cur)}</strong></div>) : <p>Žádná data.</p>}</Card></div>}
      <ServiceBar variant="seg" selects={[
        { label: 'Měna', value: currency, onChange: (value) => setCurrency(value as VyuctCurrency), options: VYUCT_CURRENCY_OPTS.map((value) => ({ value, label: value === 'ALL' ? 'Všechny' : value })) },
        { label: 'Granularita', value: granularity, onChange: (value) => setGranularity(value as VyuctGranularity), options: [{ value: 'month', label: 'Měsíc' }, { value: 'day', label: 'Den' }] },
      ]} extra={<label className="finance-switch"><input type="checkbox" checked={showCzk} onChange={(event) => setShowCzk(event.target.checked)} /><i /><span>Zobrazit vše v CZK (EUR × {FX.EUR})</span></label>} onReset={reset} />

      <div className="rondo-arc-finance-table">
        {error && <p role="alert" className="rondo-arc-finance-error">{error}</p>}
        {loading ? <PageState title="Načítám vyúčtování" loading /> : <SortableDataTable rows={tableRows} columns={columns} rowKey="key" caption="Vyúčtování uživatelů" emptyMessage="Žádná data." />}
      </div>
    </>
  );
}
